/** Links under a QuantisAI reply. Tool output can name any URL, so a Quantis
 *  source or a cited project file links only within the app, by this origin or
 *  the canonical one the server writes; only a page the server's web tools
 *  retrieved (kind 'web') may leave the app, and then visibly, in a new tab. */
import { useState } from 'react';
import type React from 'react';
import { ExternalLink, FileText } from '@/shared/ui/icons';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/ui/collapsible';
import { DEFAULT_APP_BASE_URL } from '@/document-control/policy';
import { assistantDisplayText } from './presentation';

/** A cited document also carries what Claude quoted and where; every field after kind is optional, as older replies and servers send only title and url. */
export type SISource = {
  title: string; url: string; kind?: 'web' | 'document';
  page?: number; pageEnd?: number; fileName?: string; documentId?: string; quotes?: string[];
};

const CANONICAL_ORIGIN = new URL(DEFAULT_APP_BASE_URL).origin;

function webHost(url: string): string | null {
  try { const parsed = new URL(url); return parsed.protocol === 'https:' ? parsed.hostname.replace(/^www\./, '') : null; }
  catch { return null; }
}
/** The server builds links on the canonical origin, while the iOS, Android and
 *  desktop shells run on capacitor:// or tauri://, so both count as the app. */
function appPath(url: string): string | null {
  try {
    const parsed = new URL(url);
    // Opaque origins all read "null", so a javascript: URL would match a shell whose own origin is opaque.
    if (parsed.origin === 'null') return null;
    if (parsed.origin !== window.location.origin && parsed.origin !== CANONICAL_ORIGIN) return null;
    return `${parsed.pathname}${parsed.search}`;
  } catch { return null; }
}
/** The app restores its screen from the address on popstate, so the link opens
 *  without reloading the app or leaving this chat; in a native shell a full
 *  load of the canonical URL would leave the app for the website. */
function openInApp(event: React.MouseEvent<HTMLAnchorElement>, path: string) {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  window.history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate', { state: null }));
}

/** A cited passage: a numbered marker that opens the quoted text and, when the file lives in a project, a link to the page it came from.
 *  An attached PDF has no page in the app to open, so it shows the quote alone. */
function CitedPassage({ number, source, path }: { number: number; source: SISource; path: string | null }) {
  const [open, setOpen] = useState(false);
  const target = source.page ? `page ${source.page}` : 'the document';
  return <li className="min-w-0">
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="inline-flex max-w-full items-center gap-1.5 rounded text-left text-xs outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50" aria-label={`Source ${number}: ${source.title}`}>
        <span className="inline-flex size-4 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-foreground" aria-hidden>{number}</span>
        <FileText className="size-3 shrink-0" aria-hidden />
        <span className="truncate underline">{assistantDisplayText(source.title)}</span>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-1 space-y-1 pl-6 text-xs">
        {source.quotes?.map(quote => <blockquote key={quote} className="border-l-2 border-border pl-2 text-muted-foreground [overflow-wrap:anywhere]">{quote}</blockquote>)}
        {path && <a className="inline-block underline" href={path} onClick={event => openInApp(event, path)}>Open {target} in Documents</a>}
      </CollapsibleContent>
    </Collapsible>
  </li>;
}

export function SIMessageSources({ sources }: { sources?: SISource[] }) {
  if (!sources?.length) return null;
  const inApp = (kind: SISource['kind']) => sources.flatMap(source => {
    const path = source.kind === kind ? appPath(source.url) : null;
    return path ? [{ ...source, path }] : [];
  });
  const quantis = inApp(undefined);
  // A source with quotes, or with no link at all (an attached PDF), is a citation; older ones are plain links.
  const cited = sources.flatMap(source => source.kind === 'document' && (source.quotes?.length || !source.url) ? [{ source, path: appPath(source.url) }] : []);
  const documents = inApp('document').filter(source => !(source.quotes?.length));
  const web = sources.flatMap(source => {
    const host = source.kind === 'web' ? webHost(source.url) : null;
    return host ? [{ ...source, host }] : [];
  });
  return <>
    {quantis.map(source => <a key={source.url} className="mt-2 block text-xs underline" href={source.path} onClick={event => openInApp(event, source.path)}>{assistantDisplayText(source.title)}</a>)}
    {cited.length > 0 && <ol className="mt-2 space-y-1" aria-label="Cited passages">
      {cited.map(({ source, path }, index) => <CitedPassage key={`${source.url}#${source.title}`} number={index + 1} source={source} path={path} />)}
    </ol>}
    {documents.length > 0 && <ul className="mt-2 space-y-1" aria-label="Cited project files">
      {documents.map(source => <li key={source.url} className="min-w-0">
        <a className="inline-flex max-w-full items-center gap-1 text-xs" href={source.path} onClick={event => openInApp(event, source.path)}>
          <FileText className="size-3 shrink-0" aria-hidden />
          <span className="truncate underline">{assistantDisplayText(source.title)}</span>
        </a>
      </li>)}
    </ul>}
    {web.length > 0 && <ul className="mt-2 space-y-1" aria-label="Web sources">
      {web.map(source => <li key={source.url} className="min-w-0">
        <a className="inline-flex max-w-full items-center gap-1 text-xs" href={source.url} target="_blank" rel="noopener noreferrer">
          <ExternalLink className="size-3 shrink-0" />
          <span className="truncate underline">{assistantDisplayText(source.title)}</span>
          <span className="shrink-0 text-muted-foreground">({source.host})</span>
          <span className="sr-only">(opens in a new tab)</span>
        </a>
      </li>)}
    </ul>}
  </>;
}
